import './CliToolKeyPane.less';
import { KeyOutlined } from '@ant-design/icons';
import { Alert, Button } from 'antd';
import { AxiosError } from 'axios';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';

import { useAccess } from 'hooks/useAccess';
import CopyableText from 'components/ui/CopyableText/CopyableText';
import SecretInput from 'components/ui/SecretInput/SecretInput';
import { useGenerateCliToolKey } from 'hooks/cliTerminalQueries';

export const CLI_VERSIONS_URL =
  'https://incorta.atlassian.net/wiki/spaces/INC/pages/1740898463/IC+CLI+Versions';

const REVEAL = { duration: 0.16, ease: [0.22, 1, 0.36, 1] as const };

const failureOf = (error: unknown) => {
  const failure = error as AxiosError<{ message?: string; error?: string }>;
  return (
    failure?.response?.data?.message ??
    failure?.response?.data?.error ??
    failure?.message ??
    'That token was not accepted.'
  );
};

function CliToolKeyPane({ compact = false }: { compact?: boolean }) {
  const { can } = useAccess();
  const [token, setToken] = useState('');
  const { generate, key, isPending, error, reset } = useGenerateCliToolKey();
  const canGenerate = can('cli.update');

  const submit = () => {
    const pasted = token.trim();
    if (pasted) generate(pasted);
  };

  return (
    <section
      className={`CliToolKeyPane${compact ? ' CliToolKeyPane--compact' : ''}`}
      aria-label="Your own CLI tool"
    >
      <h3 className="CliToolKeyPane__title">
        Generate the cluster token for your own CLI tool
      </h3>

      {canGenerate ? (
        <>
          {!compact && (
            <p className="CliToolKeyPane__copy">
              Paste the access token the cluster&apos;s owner generated in the
              Cloud Portal. You get the key your installed CLI logs in with,
              valid for as long as that token is.
            </p>
          )}
          <div className="CliToolKeyPane__form">
            <SecretInput
              size={compact ? 'small' : 'middle'}
              value={token}
              placeholder="Cluster access token"
              prefix={<KeyOutlined />}
              onChange={event => {
                setToken(event.target.value);
                if (error || key) reset();
              }}
              onPressEnter={submit}
            />
            <Button
              type="primary"
              size={compact ? 'small' : 'middle'}
              loading={isPending}
              disabled={!token.trim()}
              onClick={submit}
            >
              Generate
            </Button>
          </div>

          <AnimatePresence initial={false} mode="wait">
            {key && (
              <motion.div
                key="key"
                className="CliToolKeyPane__result"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={REVEAL}
              >
                <span className="CliToolKeyPane__label">Key for your CLI</span>
                <CopyableText text={key} label="Copy the CLI key" mono />
              </motion.div>
            )}
            {error && !key && (
              <motion.div
                key="error"
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={REVEAL}
              >
                <Alert type="error" showIcon message={failureOf(error)} />
              </motion.div>
            )}
          </AnimatePresence>
        </>
      ) : (
        <p className="CliToolKeyPane__copy">
          Generating a key for the installed CLI needs edit access to CLI
          Commands.
        </p>
      )}

      <p className="CliToolKeyPane__docs">
        Don&apos;t have the CLI tool yet?{' '}
        <a href={CLI_VERSIONS_URL} target="_blank" rel="noopener noreferrer">
          Get it from the CLI versions page
        </a>
      </p>
    </section>
  );
}

export default CliToolKeyPane;
